import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  AlertCircle,
  Star,
  Trash2,
  Calendar,
  Clock,
  FolderOpen,
  Search,
  X
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import RichTextEditor from '../ui/RichTextEditor';
import '../../styles/CuratorEditEvent.css';

const CuratorEditEvent = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const collectionSearchRef = useRef(null);

  const [event, setEvent] = useState(null);
  const [collection, setCollection] = useState(null);
  const [availableCollections, setAvailableCollections] = useState([]);
  const [collectionSearch, setCollectionSearch] = useState('');
  const [showCollectionDropdown, setShowCollectionDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    event_type: 'show',
    event_start: '',
    event_end: '',
    featured: false,
    capacity: '',
    ticket_price: '',
    tickets_available: '',
    ticket_sales_enabled: false,
    ticket_description: '',
    collection_id: null
  });

  // Click outside handler for collection dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (collectionSearchRef.current && !collectionSearchRef.current.contains(event.target)) {
        setShowCollectionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (eventId) {
      loadEventData();
    }
  }, [eventId]);

  const loadEventData = async () => {
    try {
      setLoading(true);

      // Verify user access
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      // Load event
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();

      if (eventError) throw eventError;
      setEvent(eventData);

      // Load all available collections for the dropdown
      const { data: collectionsData } = await supabase
        .from('collections')
        .select('collection_id, title')
        .order('title');
      setAvailableCollections(collectionsData || []);

      // Load current collection if linked
      if (eventData.collection_id) {
        const { data: collectionData } = await supabase
          .from('collections')
          .select('*')
          .eq('collection_id', eventData.collection_id)
          .single();

        if (collectionData) {
          setCollection(collectionData);
        }
      }

      // Helper to format datetime for input (YYYY-MM-DDTHH:mm)
      const formatDateForInput = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        // Format as YYYY-MM-DDTHH:mm (local time)
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };

      // Populate form
      setFormData({
        name: eventData.name || '',
        description: eventData.description || '',
        event_type: eventData.event_type || 'show',
        event_start: formatDateForInput(eventData.event_start),
        event_end: formatDateForInput(eventData.event_end),
        featured: eventData.featured || false,
        capacity: eventData.capacity || '',
        ticket_price: eventData.ticket_price || '',
        tickets_available: eventData.tickets_available || '',
        ticket_sales_enabled: eventData.ticket_sales_enabled || false,
        ticket_description: eventData.ticket_description || '',
        collection_id: eventData.collection_id || null
      });

    } catch (err) {
      console.error('Error loading event:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setMessage({ type: 'error', text: 'Event name is required' });
      return false;
    }
    if (!formData.event_start) {
      setMessage({ type: 'error', text: 'Event start date is required' });
      return false;
    }
    if (!formData.event_end) {
      setMessage({ type: 'error', text: 'Event end date is required' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // Convert local datetime-local strings to UTC ISO strings for PostgreSQL timestamptz
      const localDatetimeToUTC = (localDatetimeStr) => {
        if (!localDatetimeStr) return null;
        try {
          const localDate = new Date(localDatetimeStr);
          if (isNaN(localDate.getTime())) return localDatetimeStr;
          return localDate.toISOString();
        } catch (e) {
          return localDatetimeStr;
        }
      };

      // Update event
      const { error: updateError } = await supabase
        .from('events')
        .update({
          name: formData.name,
          description: formData.description,
          event_type: formData.event_type,
          event_start: localDatetimeToUTC(formData.event_start),
          event_end: localDatetimeToUTC(formData.event_end),
          featured: formData.featured,
          capacity: formData.capacity ? parseInt(formData.capacity) : null,
          ticket_price: formData.ticket_price || null,
          tickets_available: formData.tickets_available ? parseInt(formData.tickets_available) : null,
          ticket_sales_enabled: formData.ticket_sales_enabled,
          ticket_description: formData.ticket_description || null,
          collection_id: formData.collection_id ? parseInt(formData.collection_id) : null
        })
        .eq('id', eventId);

      if (updateError) throw updateError;

      // Sync ticket type to event_ticket_types table if ticket sales enabled
      if (formData.ticket_sales_enabled && formData.ticket_price) {
        // Check if a ticket type already exists for this event
        const { data: existingTicketTypes } = await supabase
          .from('event_ticket_types')
          .select('id')
          .eq('event_id', eventId)
          .limit(1);

        const ticketTypeData = {
          event_id: parseInt(eventId),
          ticket_type_name: 'General Admission',
          description: formData.ticket_description || 'Event admission',
          regular_price: parseFloat(formData.ticket_price) || 0,
          tickets_available: parseInt(formData.tickets_available) || parseInt(formData.capacity) || 100,
          is_active: true
        };

        if (existingTicketTypes && existingTicketTypes.length > 0) {
          // Update existing ticket type
          const { error: ticketError } = await supabase
            .from('event_ticket_types')
            .update(ticketTypeData)
            .eq('id', existingTicketTypes[0].id);
          if (ticketError) console.error('Error updating ticket type:', ticketError);
        } else {
          // Create new ticket type
          const { error: ticketError } = await supabase
            .from('event_ticket_types')
            .insert({
              ...ticketTypeData,
              tickets_sold: 0,
              stripe_product_id: `pending_${Date.now()}`,
              stripe_price_id: `pending_${Date.now()}`
            });
          if (ticketError) console.error('Error creating ticket type:', ticketError);
        }
      }

      setMessage({ type: 'success', text: 'Event updated successfully!' });

      // Navigate back - if collection was changed, go to the new collection, otherwise admin events
      const targetCollectionId = formData.collection_id || event.collection_id;
      setTimeout(() => {
        if (targetCollectionId) {
          navigate(`/curator/collections/${targetCollectionId}`);
        } else {
          navigate(`/admin/events/${eventId}`);
        }
      }, 2000);

    } catch (err) {
      console.error('Error updating event:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveEvent = async () => {
    if (!window.confirm('Remove this event? You can restore it later from the collection edit page.')) {
      return;
    }

    try {
      setSaving(true);

      const { error } = await supabase
        .from('events')
        .update({ status: 'removed' })
        .eq('id', eventId);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Event removed successfully' });

      setTimeout(() => {
        navigate(`/curator/collections/${event.collection_id}`);
      }, 2000);

    } catch (err) {
      console.error('Error removing event:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="curator-edit-event">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading event...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="curator-edit-event">
        <div className="error-state">
          <h3>Event Not Found</h3>
          <p>The event you're trying to edit doesn't exist.</p>
          <button onClick={() => navigate('/curator')} className="btn-primary">
            Back to Overview
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-edit-event">
      {/* Back Button */}
      <button
        className="back-button"
        onClick={() => {
          if (event?.collection_id) {
            navigate(`/curator/collections/${event.collection_id}`);
          } else {
            navigate(`/admin/events/${eventId}`);
          }
        }}
      >
        <ArrowLeft size={18} />
        {event?.collection_id ? 'Back to Collection' : 'Back to Event'}
      </button>

      {/* Message */}
      {message.text && (
        <div className={`message ${message.type}`}>
          <div className="message-content">
            {message.type === 'success' ? <Check size={20} /> : <AlertCircle size={20} />}
            <span>{message.text}</span>
          </div>
          <button
            className="message-close"
            onClick={() => setMessage({ type: '', text: '' })}
          >
            ×
          </button>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="edit-form">
        {/* Event Details */}
        <div className="form-section">
          <h2>Event Details</h2>

          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="name">Event Name *</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                placeholder="Event name"
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="event_type">Event Type</label>
              <select
                id="event_type"
                value={formData.event_type}
                onChange={(e) => handleFormChange('event_type', e.target.value)}
              >
                <option value="show">Art Show</option>
                <option value="opening">Opening Reception</option>
                <option value="exhibition">Exhibition</option>
                <option value="workshop">Workshop</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-field collection-search-field" ref={collectionSearchRef}>
              <label htmlFor="collection_search">
                <FolderOpen size={16} />
                Linked Collection
              </label>
              <div className="collection-search-container">
                <div className="collection-search-input-wrapper">
                  <Search size={16} className="search-icon" />
                  <input
                    id="collection_search"
                    type="text"
                    value={collectionSearch}
                    onChange={(e) => {
                      setCollectionSearch(e.target.value);
                      setShowCollectionDropdown(true);
                    }}
                    onFocus={() => setShowCollectionDropdown(true)}
                    placeholder={formData.collection_id 
                      ? availableCollections.find(c => c.collection_id == formData.collection_id)?.title || 'Search collections...'
                      : 'Search collections...'
                    }
                    autoComplete="off"
                  />
                  {formData.collection_id && (
                    <button
                      type="button"
                      className="clear-collection-btn"
                      onClick={() => {
                        handleFormChange('collection_id', null);
                        setCollectionSearch('');
                        setCollection(null);
                      }}
                      title="Remove collection link"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                {showCollectionDropdown && (
                  <div className="collection-dropdown">
                    <div
                      className={`collection-option ${!formData.collection_id ? 'selected' : ''}`}
                      onClick={() => {
                        handleFormChange('collection_id', null);
                        setCollectionSearch('');
                        setCollection(null);
                        setShowCollectionDropdown(false);
                      }}
                    >
                      <span className="no-collection">No Collection</span>
                    </div>
                    {availableCollections
                      .filter(col => 
                        col.title.toLowerCase().includes(collectionSearch.toLowerCase())
                      )
                      .map((col) => (
                        <div
                          key={col.collection_id}
                          className={`collection-option ${formData.collection_id == col.collection_id ? 'selected' : ''}`}
                          onClick={() => {
                            handleFormChange('collection_id', col.collection_id);
                            setCollectionSearch('');
                            setCollection(col);
                            setShowCollectionDropdown(false);
                          }}
                        >
                          {col.title}
                        </div>
                      ))}
                    {availableCollections.filter(col => 
                      col.title.toLowerCase().includes(collectionSearch.toLowerCase())
                    ).length === 0 && collectionSearch && (
                      <div className="collection-option no-results">
                        No collections match "{collectionSearch}"
                      </div>
                    )}
                  </div>
                )}
              </div>
              {formData.collection_id && (
                <div className="selected-collection-badge">
                  <FolderOpen size={14} />
                  {availableCollections.find(c => c.collection_id == formData.collection_id)?.title}
                </div>
              )}
            </div>
          </div>

          <div className="form-field">
            <label htmlFor="description">Description</label>
            <RichTextEditor
              value={formData.description}
              onChange={(content) => handleFormChange('description', content)}
              placeholder="Describe the event..."
              minHeight="150px"
            />
          </div>

          <div className="form-field checkbox-field">
            <label>
              <input
                type="checkbox"
                checked={formData.featured}
                onChange={(e) => handleFormChange('featured', e.target.checked)}
              />
              <Star size={16} />
              Featured Event
            </label>
          </div>
        </div>

        {/* Event Dates */}
        <div className="form-section">
          <h2>Exhibition Dates</h2>

          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="event_start">Event Start *</label>
              <div className="input-with-icon">
                <Calendar size={18} />
                <input
                  id="event_start"
                  type="datetime-local"
                  value={formData.event_start}
                  onChange={(e) => handleFormChange('event_start', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="event_end">Event End *</label>
              <div className="input-with-icon">
                <Calendar size={18} />
                <input
                  id="event_end"
                  type="datetime-local"
                  value={formData.event_end}
                  onChange={(e) => handleFormChange('event_end', e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* Capacity & Ticketing */}
        <div className="form-section">
          <h2>Capacity & Ticketing</h2>

          <div className="form-field">
            <label htmlFor="capacity">Capacity</label>
            <input
              id="capacity"
              type="number"
              value={formData.capacity}
              onChange={(e) => handleFormChange('capacity', e.target.value)}
              placeholder="Maximum attendees"
            />
          </div>

          <div className="form-field checkbox-field">
            <label>
              <input
                type="checkbox"
                checked={formData.ticket_sales_enabled}
                onChange={(e) => handleFormChange('ticket_sales_enabled', e.target.checked)}
              />
              Enable Ticket Sales
            </label>
          </div>

          {formData.ticket_sales_enabled && (
            <div className="ticket-fields">
              <div className="form-grid">
                <div className="form-field">
                  <label htmlFor="ticket_price">Ticket Price ($)</label>
                  <input
                    id="ticket_price"
                    type="number"
                    step="0.01"
                    value={formData.ticket_price}
                    onChange={(e) => handleFormChange('ticket_price', e.target.value)}
                    placeholder="0.00"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="tickets_available">Tickets Available</label>
                  <input
                    id="tickets_available"
                    type="number"
                    value={formData.tickets_available}
                    onChange={(e) => handleFormChange('tickets_available', e.target.value)}
                    placeholder="Number of tickets"
                  />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="ticket_description">Ticket Description</label>
                <textarea
                  id="ticket_description"
                  value={formData.ticket_description}
                  onChange={(e) => handleFormChange('ticket_description', e.target.value)}
                  placeholder="Ticket details, what's included..."
                  rows={2}
                />
              </div>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-danger"
            onClick={handleRemoveEvent}
            disabled={saving}
          >
            <Trash2 size={16} />
            Remove Event
          </button>
          <div className="actions-right">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate(`/curator/collections/${event.collection_id}`)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="loading-spinner small"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CuratorEditEvent;
